import { resolveRealtimeVoiceDispatchId } from '@/lib/realtime-voice-destination';
import { answerRealtimeVoiceDispatchQuestion } from './realtime-voice-dispatch-question';
import { claimLiveVoiceToolOperation } from '@/lib/live-voice-tool-operations';
import { VOICE_TOOL_DESCRIPTIONS } from '@/lib/live-voice-tool-definitions';
import { tool } from '@openai/agents/realtime';
import type { RealtimeVoiceToolContext } from '@/components/org-chat/realtime-voice-tool-context';
import {
  answerCurrentQuestionSchema,
  normalizeGroupedAnswersForConvex,
} from '@/components/org-chat/voice-call-tool-input';
import {
  getActiveRealtimeVoiceQuestionnaire,
  getRealtimeQuestionnaireToolResponseText,
  getRealtimeVoiceAnswerText,
  normalizeActiveRealtimeQuestionnaireAnswerInput,
} from '@/lib/realtime-voice-questionnaire';

const OFF_PAGE_QUESTION_REQUIRES_CHAT =
  'A question form is waiting in the original chat. Ask the user to open that chat to complete it. Do not submit an answer here.';

export function createRealtimeVoiceQuestionnaireTool(
  context: RealtimeVoiceToolContext
) {
  return tool({
    name: 'answer_current_question',
    description: VOICE_TOOL_DESCRIPTIONS.answer_current_question,
    parameters: answerCurrentQuestionSchema,
    strict: true,
    errorFunction: null,
    execute: async (input) => {
      if (!context.isCurrentCall()) {
        return 'Internal safety: this voice call is no longer active. Stay silent.';
      }
      const parsed = answerCurrentQuestionSchema.parse(input);
      if (parsed.dispatchId?.startsWith('ot_'))
        return context.finishRealtimeToolResult({
          toolName: 'answer_current_question',
          result: JSON.stringify({
            status: 'unavailable',
            reason: 'not_a_dispatch',
            threadId: parsed.dispatchId,
          }),
        });
      if (!claimLiveVoiceToolOperation(context, 'questionnaire')) {
        return 'Internal safety: this delegated operation already attempted a question answer. Do not call answer_current_question again until the user answers out loud. Ask the visible question if needed, otherwise stay silent.';
      }
      const questionnaire = getActiveRealtimeVoiceQuestionnaire(
        String(context.orgThreadId)
      );
      try {
        const dispatchId = resolveRealtimeVoiceDispatchId(
          parsed,
          context.voiceDestinationRef?.current ?? null,
          context.voiceQuestionRef?.current
        );
        if (parsed.scope === 'destination' && !dispatchId)
          return context.finishRealtimeToolResult({
            toolName: 'answer_current_question',
            result: context.askQuestionCopy.voiceNoPendingQuestion,
          });
        if (dispatchId)
          return await answerRealtimeVoiceDispatchQuestion(
            context,
            parsed,
            dispatchId
          );
        const sourceChatVisible = context.isSourceChatVisible?.() ?? true;
        if (
          sourceChatVisible &&
          questionnaire &&
          (!parsed.toolCallId || parsed.toolCallId === questionnaire.toolCallId)
        ) {
          const normalizedInput =
            normalizeActiveRealtimeQuestionnaireAnswerInput(
              parsed,
              questionnaire.question,
              questionnaire.questions ?? [questionnaire.question]
            );
          const activeQuestionIds = new Set(
            (questionnaire.questions ?? [questionnaire.question]).map(
              (question) => question.id
            )
          );
          if (
            !normalizedInput.skipCurrentQuestion &&
            !normalizedInput.questionnaireNavigation &&
            !Object.keys(normalizedInput.groupAnswers ?? {}).some(
              (questionId) => activeQuestionIds.has(questionId)
            )
          ) {
            return context.finishRealtimeToolResult({
              toolName: 'answer_current_question',
              result:
                'I did not catch the answer. Ask the question again in one short sentence.',
            });
          }
          const localResult =
            await questionnaire.answerCurrentStep(normalizedInput);
          const silent =
            getRealtimeQuestionnaireToolResponseText(localResult.message) ===
            '';
          if (
            silent &&
            context.voiceQuestionRef?.current?.scope === 'source' &&
            context.voiceQuestionRef.current.toolCallId ===
              questionnaire.toolCallId
          )
            context.voiceQuestionRef.current = null;
          return context.finishRealtimeToolResult({
            toolName: 'answer_current_question',
            silent,
            result: getRealtimeQuestionnaireToolResponseText(
              localResult.message
            ),
          });
        }

        if (
          (!sourceChatVisible || !questionnaire) &&
          context.answerDispatchQuestion
        )
          return await answerRealtimeVoiceDispatchQuestion(context, parsed);

        if (parsed.skipCurrentQuestion) {
          return context.finishRealtimeToolResult({
            toolName: 'answer_current_question',
            result:
              'This visible question cannot be skipped here. Tell the user briefly that it needs an answer.',
          });
        }

        // Group drafts and confirmation live in the mounted chat card. The
        // backend group action would retire an incomplete off-page answer.
        if (!sourceChatVisible && parsed.groupAnswers) {
          return context.finishRealtimeToolResult({
            toolName: 'answer_current_question',
            result: OFF_PAGE_QUESTION_REQUIRES_CHAT,
          });
        }
        let verifiedToolCallId = parsed.toolCallId;
        if (!sourceChatVisible && context.getOffPageQuestion) {
          const { pendingQuestion } = await context.getOffPageQuestion({
            orgThreadId: context.orgThreadId,
            sessionId: context.leaseSessionId,
          });
          if (!context.isCurrentCall()) {
            return 'Internal safety: this voice call is no longer active. Stay silent.';
          }
          if (pendingQuestion?.kind === 'open-chat') {
            return context.finishRealtimeToolResult({
              toolName: 'answer_current_question',
              result: OFF_PAGE_QUESTION_REQUIRES_CHAT,
            });
          }
          if (
            !pendingQuestion ||
            (parsed.toolCallId &&
              parsed.toolCallId !== pendingQuestion.toolCallId)
          ) {
            return context.finishRealtimeToolResult({
              toolName: 'answer_current_question',
              result: context.askQuestionCopy.voiceNoPendingQuestion,
            });
          }
          verifiedToolCallId = pendingQuestion.toolCallId;
        }
        const answerText = getRealtimeVoiceAnswerText(parsed);
        if (!answerText) {
          return context.finishRealtimeToolResult({
            toolName: 'answer_current_question',
            result:
              'I did not catch the answer. Ask the question again in one short sentence.',
          });
        }
        const groupAnswers = sourceChatVisible
          ? normalizeGroupedAnswersForConvex(parsed.groupAnswers)
          : null;
        const result = groupAnswers
          ? await context.answerAskQuestionGroup({
              orgThreadId: context.orgThreadId,
              answerText,
              answers: groupAnswers,
              ...(verifiedToolCallId ? { toolCallId: verifiedToolCallId } : {}),
            })
          : await context.answerAskQuestion({
              orgThreadId: context.orgThreadId,
              answerText,
              ...(parsed.answerValue
                ? { answerValue: parsed.answerValue }
                : {}),
              ...(verifiedToolCallId ? { toolCallId: verifiedToolCallId } : {}),
            });
        if ('registeredFormRequired' in result && result.registeredFormRequired)
          return context.finishRealtimeToolResult({
            toolName: 'answer_current_question',
            result: context.askQuestionCopy.voiceRegisteredFormRequiresChat,
          });
        if (!result.patched) {
          return context.finishRealtimeToolResult({
            toolName: 'answer_current_question',
            result: context.askQuestionCopy.voiceNoPendingQuestion,
          });
        }

        return context.finishRealtimeToolResult({
          toolName: 'answer_current_question',
          silent: result.resumed,
          result: result.resumed
            ? context.askQuestionCopy.voiceAnswerResumedSilent
            : context.askQuestionCopy.voiceSavedWithoutResume,
        });
      } catch (error) {
        console.error('Failed to submit realtime voice questionnaire answer', {
          errorName: error instanceof Error ? error.name : 'UnknownError',
        });

        return context.finishRealtimeToolResult({
          toolName: 'answer_current_question',
          result: context.askQuestionCopy.voiceAnswerFailed,
        });
      }
    },
  });
}
